import { AlarmClock, ChartArea, Group, LucideUsers2, NfcIcon, Settings, MessageSquareText, X, ChartSpline } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

interface SidebarItems {
  title: string
  icon: React.ReactNode
  link: string,
  adminOnly?: boolean
}

interface SidebarProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (value: boolean) => void;
}

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen }: SidebarProps) => {
  const location = useLocation();
  const sidebarItems: SidebarItems[] = [
    {
      title: 'Dashboard',
      icon: <ChartArea />,
      link: '/dashboard'
    },
    {
      title: 'Users',
      icon: <LucideUsers2 />,
      link: '/users'
    },
    {
      title: 'Alarms',
      icon: <AlarmClock />,
      link: '/alarms'
    },
    {
      title: 'Leagues',
      icon: <Group />,
      link: '/leagues'
    },
    {
      title: 'MotiTags',
      icon: <NfcIcon />,
      link: '/moti_tags'
    },
    {
      title: 'Reports',
      icon: <ChartSpline />,
      link: '/reports'
    },
    {
      title: 'User Feedback',
      icon: <MessageSquareText />,
      link: '/user_feedback'
    },
    {
      title: 'App Settings',
      icon: <Settings />,
      link: '/settings'
    },
  ];

  const handleCloseSidebar = () => {
    if(isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/30 z-30 md:hidden"
          onClick={handleCloseSidebar}
        />
      )}
      
      <aside className={`
        fixed md:relative
        top-0 md:top-0
        left-0
        h-full
        w-full max-w-xs
        bg-white border-r border-gray-200/80
        transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
        md:w-[280px]
        transition-transform duration-300 ease-in-out
        z-40 md:z-0
        shadow-xl md:shadow-none
        flex flex-col
      `}>
        
        {/* Mobile Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200/80 md:hidden">
          <h2 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            Menu
          </h2>
          <button
            onClick={handleCloseSidebar}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
          {sidebarItems.map((item, index) => (
            <SidebarItem
              key={index}
              title={item.title}
              icon={item.icon}
              link={item.link}
              adminOnly={item.adminOnly}
              handleCloseSidebar={handleCloseSidebar}
              isActive={location.pathname.startsWith(item.link) || (item.link === '/dashboard' && location.pathname === '/')}
            />
          ))}
        </nav>
      </aside>
    </>
  );
};

const SidebarItem = ({ title, icon, link, isActive, adminOnly, handleCloseSidebar }: {
  title: string,
  icon: React.ReactNode,
  link: string,
  isActive: boolean,
  adminOnly: boolean | undefined
  handleCloseSidebar: () => void
}) => {

  if (adminOnly) {
    return null
  }

  return (
    <Link
      to={link}
      onClick={handleCloseSidebar}
      className={`group flex items-center gap-3.5 py-3 px-4 rounded-xl font-medium text-sm transition-all duration-200 ${
        isActive 
          ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg shadow-purple-500/30' 
          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
      }`}
    >
      <div className={`flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-purple-500'} transition-colors duration-200`}>
        {icon}
      </div>
      <span className="flex-1">{title}</span>
      {isActive && (
        <div className="w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse" />
      )}
    </Link>
  );
};

export default Sidebar;